import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import { Card, CardContent } from "@/shared/ui/card";
import { exportPropertyList, type PropertyExportFormat } from "@/features/exports/services";
import { toast } from "@/shared/hooks/use-toast";
import { useExportsEnabled } from "@/shared/hooks/useExportsEnabled";
import {
  getGroupedPropertiesPage,
  getPropertiesCount,
  getPropertiesPage,
  getPortfolioProgress,
  getStageCounts,
  getTeams,
  type GroupedPropertiesResult,
  type PortfolioProgress as PortfolioProgressData,
  type PropertyTeam,
} from "@/features/properties/services/clientPropertiesQueries";
import type { Property } from "@/shared/types/client";
import { isGroupedPropertySearch } from "@/features/properties/services/propertySearch";
import { usePropertySearchStore } from "@/features/properties/services/propertySearchStore";
import { usePropertyPaginationStore, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from "@/features/properties/services/propertyPaginationStore";
import { DEFAULT_SORT, EMPTY_FILTERS, hasActiveFilters, type PropertyFilters, type PropertySort } from "@/features/properties/services/propertyFilters";
import type { StatusFilter } from "@/features/properties/services/deliveryStatusConfig";

import { PropertiesHeader } from "./clientPropertiesSection/PropertiesHeader";
import { PortfolioProgress } from "./clientPropertiesSection/PortfolioProgress";
import { PropertiesFilterBar } from "./clientPropertiesSection/PropertiesFilterBar";
import { PropertiesTable } from "./clientPropertiesSection/PropertiesTable";
import { PropertiesSkeleton } from "./clientPropertiesSection/PropertiesSkeleton";
import { SectionPagination } from "./clientPropertiesSection/SectionPagination";
import { getPaginationSlots } from "./clientPropertiesSection/pagination";

interface ClientPropertiesSectionProps {
  clientId: string;
  projects: ClientProjectSummary[];
  onAddProperty: () => void;
  onImportCSV?: () => void;
}

type ClientProjectSummary = {
  id: string;
  name?: string | null;
  logo_url?: string | null;
};

export function ClientPropertiesSection({
  clientId,
  projects,
  onAddProperty,
  onImportCSV,
}: ClientPropertiesSectionProps) {
  const navigate = useNavigate();

  const [properties, setProperties] = useState<Property[]>([]);
  const [groupedResults, setGroupedResults] = useState<GroupedPropertiesResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedProperties, setHasLoadedProperties] = useState(false);
  const [currentPage, setCurrentPage] = useState(
    () => usePropertyPaginationStore.getState().paginationByClientId[clientId]?.page ?? DEFAULT_PAGE
  );
  const [totalCount, setTotalCount] = useState(0);
  // Unfiltered property inventory for the header "N total"; independent of the
  // active search/status/deliverable filters (which narrow the list below).
  const [grandTotalCount, setGrandTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(
    () => usePropertyPaginationStore.getState().paginationByClientId[clientId]?.pageSize ?? DEFAULT_PAGE_SIZE
  );
  const persistPage = usePropertyPaginationStore((state) => state.setPage);
  const persistPageSize = usePropertyPaginationStore((state) => state.setPageSize);
  const [exportingFormat, setExportingFormat] = useState<PropertyExportFormat | null>(null);
  const { enabled: exportsEnabled } = useExportsEnabled(clientId);
  const searchTerm = usePropertySearchStore((state) => state.searchTermsByClientId[clientId] ?? "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(() => searchTerm.trim());
  const setStoredSearchTerm = usePropertySearchStore((state) => state.setSearchTerm);
  const [filters, setFilters] = useState<PropertyFilters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<PropertySort>(DEFAULT_SORT);
  const [stageCounts, setStageCounts] = useState<Record<StatusFilter, number>>({ no_survey_scheduled: 0, survey_scheduled: 0, in_progress: 0, delivered: 0 });
  const [portfolioProgress, setPortfolioProgress] = useState<PortfolioProgressData | null>(null);
  const [teams, setTeams] = useState<PropertyTeam[]>([]);
  const isSearching = debouncedSearchTerm.trim().length > 0 || hasActiveFilters(filters);
  const isGroupedSearch = isGroupedPropertySearch(debouncedSearchTerm);
  const groupedTotalCount = groupedResults.reduce((sum, group) => sum + group.count, 0);

  const handleFiltersChange = useCallback((next: PropertyFilters) => { setFilters(next); setCurrentPage(1); }, []);
  const handleSortChange = useCallback((next: PropertySort) => { setSort(next); setCurrentPage(1); }, []);

  // Clicking a portfolio-progress segment filters the table to just that stage,
  // reusing the existing stage-filter plumbing (preserves search/team/deliverable filters).
  const handleStageClick = useCallback((stage: StatusFilter) => {
    setFilters((prev) => ({ ...prev, stages: { [stage]: "has" } }));
    setCurrentPage(1);
  }, []);

  const handleSearchTermChange = useCallback((term: string) => {
    setStoredSearchTerm(clientId, term);
    setCurrentPage(1);
  }, [clientId, setStoredSearchTerm]);

  const handleExport = async (format: PropertyExportFormat) => {
    if (!clientId) return;
    setExportingFormat(format);
    try {
      const label = projects.find((p) => p.id === clientId)?.name || "properties";
      await exportPropertyList({
        filters: { projectId: clientId },
        label,
        format,
      });
      toast({ title: `Property list exported as ${format.toUpperCase()}` });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setExportingFormat(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginationSlots = getPaginationSlots(currentPage, totalPages);

  const currentClient = projects.find((p) => p.id === clientId);
  const clientLogo = currentClient?.logo_url;
  const deliveredPercent =
    portfolioProgress && portfolioProgress.total > 0
      ? Math.round((portfolioProgress.stages.delivered / portfolioProgress.total) * 100)
      : null;

  // Persist page + page size per client so navigating back restores them.
  useEffect(() => { persistPage(clientId, currentPage); }, [clientId, currentPage, persistPage]);
  useEffect(() => { persistPageSize(clientId, pageSize); }, [clientId, pageSize, persistPageSize]);

  // A restored page can exceed the current range (data changed since last visit) — clamp it.
  useEffect(() => {
    if (hasLoadedProperties && !loading && currentPage > totalPages) setCurrentPage(totalPages);
  }, [hasLoadedProperties, loading, currentPage, totalPages]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (!clientId) return;
    getStageCounts(clientId, debouncedSearchTerm).then(setStageCounts).catch(() => {});
    getTeams(clientId).then(setTeams).catch(() => {});
  }, [clientId, debouncedSearchTerm]);

  useEffect(() => {
    if (!clientId) return;
    getPropertiesCount(clientId).then(setGrandTotalCount).catch(() => {});
  }, [clientId]);

  // Whole-portfolio progress for the hero band — independent of search/filters.
  useEffect(() => {
    if (!clientId) return;
    getPortfolioProgress(clientId).then(setPortfolioProgress).catch(() => {});
  }, [clientId]);

  useEffect(() => {
    const fetchProperties = async () => {
      if (!clientId) return;

      setLoading(true);
      try {
        if (isGroupedSearch) {
          const groups = await getGroupedPropertiesPage(clientId, debouncedSearchTerm, pageSize);
          const count = groups.reduce((sum, group) => sum + group.count, 0);

          setGroupedResults(groups);
          setTotalCount(count);
          setProperties([]);
          return;
        }

        const from = (currentPage - 1) * pageSize;
        const to = from + pageSize - 1;

        const [count, transformedProperties] = await Promise.all([
          getPropertiesCount(clientId, debouncedSearchTerm, filters),
          getPropertiesPage(clientId, from, to, debouncedSearchTerm, filters, sort),
        ]);

        setTotalCount(count);
        setProperties(transformedProperties);
        setGroupedResults([]);
      } catch (error) {
        console.error("Error fetching properties:", error);
      } finally {
        setLoading(false);
        setHasLoadedProperties(true);
      }
    };

    fetchProperties();
  }, [clientId, currentPage, debouncedSearchTerm, filters, isGroupedSearch, pageSize, sort]);

  const filteredProperties = properties;

  const handlePropertyClick = (propertyId: string) => {
    navigate(`/property/${propertyId}`);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  if (loading && !hasLoadedProperties) {
    return <PropertiesSkeleton />;
  }

  const countSummary = isGroupedSearch
    ? `${groupedResults.length} groups · ${groupedTotalCount} matching ${groupedTotalCount === 1 ? "property" : "properties"}`
    : totalCount === 0
      ? "No properties"
      : `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalCount)} of ${totalCount}`;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PropertiesHeader
        clientLogo={clientLogo}
        clientName={currentClient?.name ?? undefined}
        totalCount={grandTotalCount}
        deliveredPercent={deliveredPercent}
        exportsEnabled={!!exportsEnabled}
        exportingFormat={exportingFormat}
        onExport={handleExport}
        onMoreExports={() => navigate(`/exports`)}
        onAddProperty={onAddProperty}
        onImportCSV={onImportCSV}
      />

      <PortfolioProgress
        clientId={clientId}
        progress={portfolioProgress}
        onStageClick={handleStageClick}
      />

      <PropertiesFilterBar
        clientId={clientId}
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTermChange}
        onPropertySelect={handlePropertyClick}
        filters={filters}
        stageCounts={stageCounts}
        teams={teams}
        onFiltersChange={handleFiltersChange}
      />

      <div className="flex-1 overflow-y-auto px-2 py-1 space-y-4">
        {(isGroupedSearch ? groupedResults.length === 0 : filteredProperties.length === 0) ? (
          <Card className="glass-effect border-border/50">
            <CardContent className="py-16 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted/30">
                <Building2 className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="mb-2 text-lg font-semibold">{isSearching ? "No Results Found" : "No Properties Yet"}</h3>
              <p className="text-muted-foreground">
                {isSearching ? "Try a different search or clear your filters" : "Add properties to start managing your portfolio"}
              </p>
            </CardContent>
          </Card>
        ) : isGroupedSearch ? (
          groupedResults.map((group) => (
            <section key={group.label} className="space-y-2 pb-2">
              <div className="rounded-lg border border-border/50 bg-card/80 px-4 py-2">
                <h3 className="font-semibold text-foreground">{group.label}</h3>
                <p className="text-xs text-muted-foreground">
                  {group.count} {group.count === 1 ? "matching property" : "matching properties"}
                </p>
              </div>
              <PropertiesTable properties={group.properties} sort={sort} onSortChange={handleSortChange} onRowClick={handlePropertyClick} />
            </section>
          ))
        ) : (
          <PropertiesTable properties={filteredProperties} sort={sort} onSortChange={handleSortChange} onRowClick={handlePropertyClick} />
        )}
      </div>

      <SectionPagination
        isGroupedSearch={isGroupedSearch}
        countSummary={countSummary}
        loading={loading && hasLoadedProperties}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        currentPage={currentPage}
        totalPages={totalPages}
        paginationSlots={paginationSlots}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}
